import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Sparkles } from "lucide-react";

const HIDDEN_ROUTES = ["/booking", "/checkout", "/auth/callback", "/dashboard", "/boutique"];

// Routes du domaine "Séjours & Retraites" → CTA mobile spécifique.
// On matche par prefix pour couvrir /retraites, /retraite-spirituelle-drome-provence,
// /retraite-spirituelle-sud-france. La page détail d'un séjour (/retraites/:slug)
// est explicitement EXCLUE : le module de résa est déjà inline, un CTA global
// créerait de la confusion sur "où cliquer pour réserver".
const RETREAT_ROUTES = ["/retraites", "/retraite-spirituelle"];

/**
 * Sticky bottom action button for mobile only.
 * - Appears after a small scroll so it doesn't clutter the hero.
 * - Hidden on booking/checkout/auth/dashboard routes.
 * - Contextuel : "Réserver un séjour" sur les pages retraite, "Réserver une
 *   consultation" partout ailleurs.
 * - Sur `/retraites/:slug` : scroll doux vers `#reservation` (module intégré)
 *   plutôt qu'un nouveau navigate qui ferait perdre le contexte.
 * - Bottom padding accounts for safe-area-inset on iPhones.
 */
export function MobileBookingCTA() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const onHiddenRoute = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));
  const onRetreatRoute = RETREAT_ROUTES.some((r) => location.pathname.startsWith(r));
  // Sous-cas : page détail d'un séjour → on masque le CTA car un module de résa
  // est déjà présent dans la page (évite la confusion "où cliquer pour réserver").
  const isRetreatDetail = /^\/retraites\/[^/]+$/.test(location.pathname)
    && !location.pathname.startsWith("/retraites/reservation");

  useEffect(() => {
    if (onHiddenRoute || isRetreatDetail) {
      setVisible(false);
      return;
    }
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.6);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [onHiddenRoute, isRetreatDetail, location.pathname]);

  if (onHiddenRoute || isRetreatDetail) return null;

  const handleClick = () => {
    if (onRetreatRoute) {
      // Sur la page pilier /retraites → scroll doux vers les prochaines dates.
      const el = document.getElementById("prochaines-dates");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate(onRetreatRoute ? "/retraites#prochaines-dates" : "/booking");
  };

  const label = onRetreatRoute ? "Réserver un séjour" : "Réserver une consultation";
  const Icon = onRetreatRoute ? Sparkles : Calendar;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="md:hidden fixed bottom-0 inset-x-0 z-40 pointer-events-none"
          data-testid="mobile-booking-cta"
        >
          {/* Subtle gradient mask above for legibility over light content */}
          <div className="h-6 bg-gradient-to-t from-[#1B132B] to-transparent pointer-events-none" />
          <div
            className="bg-[#1B132B]/95 backdrop-blur-md border-t border-white/10 px-4 pt-3 pointer-events-auto"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={handleClick}
              className={`w-full flex items-center justify-center gap-2 font-medium py-3.5 rounded-full active:scale-[0.98] transition-transform ${
                onRetreatRoute
                  ? "bg-[#F5D8A2] text-[#2a2418] shadow-[0_4px_20px_rgba(245,216,162,0.35)]"
                  : "gradient-primary text-white shadow-[0_4px_20px_rgba(217,38,169,0.45)]"
              }`}
              data-testid="mobile-booking-cta-btn"
              aria-label={label}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm uppercase tracking-wider">{label}</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
